// 5a · Mes liens : un lien à jeton par widget (JSON pour DashLife, embed pour iframe). Copier,
// régénérer (nouveau jeton, compteurs remis à zéro), expiration, révocation définitive.
// Cartes sur mobile, vrai tableau sur bureau.
import type { LinkDto, TableDto } from '@crenow/shared';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../api.ts';
import { parisDay, shortDay, sinceLabel, thousands } from '../format.ts';
import { Empty, FormError, Loading, TabBar, useFlash, useResource } from '../ui.tsx';

const DAY_MS = 86_400_000;
const WARN_DAYS = 7;
/** Cycle du menu : jamais → 30 j → 90 j → jamais. */
const EXPIRY_STEPS = [null, 30, 90] as const;

const urls = (token: string) => ({ json: `${window.location.origin}/t/${token}.json`, embed: `${window.location.origin}/t/${token}/embed` });

function expiry(link: LinkDto, now: Date): { label: string; tone: 'ink' | 'imm' | 'danger' } {
  if (link.revoked) return { label: '—', tone: 'ink' };
  if (!link.expiresAt) return { label: 'Jamais', tone: 'ink' };
  const at = new Date(link.expiresAt);
  if (at <= now) return { label: 'Expiré', tone: 'danger' };
  return { label: shortDay(parisDay(at)), tone: at.getTime() - now.getTime() < WARN_DAYS * DAY_MS ? 'imm' : 'ink' };
}

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Contexte non sécurisé ou permission refusée : copie par sélection.
    const area = document.createElement('textarea');
    area.value = text;
    document.body.append(area);
    area.select();
    document.execCommand('copy');
    area.remove();
  }
}

export function LinksPage() {
  const links = useResource<{ links: LinkDto[] }>('/links');
  const tables = useResource<{ tables: TableDto[] }>('/tables');
  const [creating, setCreating] = useState(false);
  const [menu, setMenu] = useState<number | null>(null);
  const [copied, flashCopied] = useFlash(1400);
  const [error, setError] = useState<string | null>(null);
  const now = new Date();

  const replace = (next: LinkDto) => links.setData((current) => current && { links: current.links.map((link) => (link.id === next.id ? next : link)) });

  async function act(link: LinkDto, task: () => Promise<{ link: LinkDto }>) {
    setMenu(null);
    setError(null);
    try {
      replace((await task()).link);
    } catch (caught) {
      setError(`${link.name} : ${errorMessage(caught)}`);
    }
  }

  const actions = {
    copy: async (link: LinkDto, kind: 'json' | 'embed') => {
      await copy(urls(link.token)[kind]);
      flashCopied(`${link.id}-${kind}`);
    },
    regenerate: (link: LinkDto) => {
      if (window.confirm(`Régénérer l’URL de « ${link.name} » ? L’ancienne cessera de répondre.`)) void act(link, () => api('POST', `/links/${link.id}/regenerate`));
    },
    cycleExpiry: (link: LinkDto) => {
      const days = link.expiresAt ? Math.round((new Date(link.expiresAt).getTime() - Date.now()) / DAY_MS) : null;
      const index = days === null ? 0 : days <= 30 ? 1 : 2;
      const next = EXPIRY_STEPS[(index + 1) % EXPIRY_STEPS.length]!;
      void act(link, () => api('PATCH', `/links/${link.id}`, { expiresAt: next === null ? null : new Date(Date.now() + next * DAY_MS).toISOString() }));
    },
    revoke: (link: LinkDto) => {
      if (window.confirm(`Révoquer « ${link.name} » ? Le lien ne répondra plus, définitivement.`)) void act(link, () => api('PATCH', `/links/${link.id}`, { revoked: true }));
    },
  };

  const list = links.data?.links ?? [];

  return (
    <div className="screen links">
      <header className="screen-head compact">
        <div className="head-row">
          <h1 className="h1">Mes liens</h1>
          <button type="button" className="btn btn-primary btn-38" aria-expanded={creating} onClick={() => setCreating(!creating)}>
            + Nouveau
          </button>
        </div>
      </header>
      <main className="screen-body">
        {creating && tables.data && (
          <NewLink
            tables={tables.data.tables}
            onCreated={(link) => {
              links.setData((current) => ({ links: [link, ...(current?.links ?? [])] }));
              setCreating(false);
            }}
          />
        )}
        <FormError error={error ?? links.error} />
        <p className="sr-only" role="status">
          {copied ? 'URL copiée' : ''}
        </p>
        {!links.data && !links.error && <Loading />}
        {links.data && list.length === 0 && !creating && (
          <Empty>
            <span>Un lien donne accès en lecture à un tableau, sans compte : à coller dans un widget DashLife ou une iframe.</span>
            <button type="button" className="btn btn-primary btn-48" onClick={() => setCreating(true)}>
              Créer un lien
            </button>
          </Empty>
        )}
        <div className="link-cards">
          {list.map((link) => (
            <LinkCard key={link.id} link={link} now={now} copied={copied} menuOpen={menu === link.id} onMenu={(open) => setMenu(open ? link.id : null)} actions={actions} />
          ))}
        </div>
        {list.length > 0 && <LinkTable links={list} now={now} copied={copied} actions={actions} />}
      </main>
      <TabBar />
    </div>
  );
}

type Actions = {
  copy: (link: LinkDto, kind: 'json' | 'embed') => Promise<void>;
  regenerate: (link: LinkDto) => void;
  cycleExpiry: (link: LinkDto) => void;
  revoke: (link: LinkDto) => void;
};

function CopyButtons({ link, copied, actions }: { link: LinkDto; copied: string | null; actions: Actions }) {
  return (
    <div className="copy-buttons">
      {(['json', 'embed'] as const).map((kind) => {
        const done = copied === `${link.id}-${kind}`;
        return (
          <button key={kind} type="button" className={`btn btn-secondary btn-40${done ? ' copied' : ''}`} onClick={() => void actions.copy(link, kind)}>
            {done ? 'Copié ✓' : kind === 'json' ? 'Copier URL JSON' : 'Copier URL embed'}
          </button>
        );
      })}
    </div>
  );
}

function LinkMenu({ link, onClose, actions }: { link: LinkDto; onClose: () => void; actions: Actions }) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    ref.current?.querySelector('button')?.focus();
    const away = (event: MouseEvent) => {
      if (!ref.current?.parentElement?.contains(event.target as Node)) close.current();
    };
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && close.current();
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', escape);
    };
  }, []);
  return (
    <div className="menu" role="menu" ref={ref}>
      <button type="button" role="menuitem" onClick={() => actions.regenerate(link)}>
        Régénérer l’URL
      </button>
      <button type="button" role="menuitem" onClick={() => actions.cycleExpiry(link)}>
        Expiration : {expiry(link, new Date()).label}
      </button>
      <button type="button" role="menuitem" className="danger" onClick={() => actions.revoke(link)}>
        Révoquer
      </button>
    </div>
  );
}

function LinkCard({ link, now, copied, menuOpen, onMenu, actions }: { link: LinkDto; now: Date; copied: string | null; menuOpen: boolean; onMenu: (open: boolean) => void; actions: Actions }) {
  const exp = expiry(link, now);
  return (
    <article className={`card link-card${link.revoked ? ' revoked' : ''}`}>
      <div className="head-row top dim">
        <div className="stack">
          <span className="card-title">{link.name}</span>
          <span className="card-sub">→ {link.tableName}</span>
        </div>
        {!link.revoked && (
          <button type="button" className="icon-btn" aria-label={`Actions pour ${link.name}`} aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => onMenu(!menuOpen)}>
            ⋯
          </button>
        )}
      </div>
      <dl className="link-stats dim">
        <div>
          <dt>Dernier usage</dt>
          <dd>{sinceLabel(link.lastUsedAt, now)}</dd>
        </div>
        <div>
          <dt>Appels</dt>
          <dd className="mono">{thousands(link.hits)}</dd>
        </div>
        <div>
          <dt>Expiration</dt>
          <dd className={`tone-${exp.tone}`}>{exp.label}</dd>
        </div>
      </dl>
      {!link.revoked && (
        <div className="link-urls">
          <span className="link-url">{urls(link.token).json}</span>
          <CopyButtons link={link} copied={copied} actions={actions} />
        </div>
      )}
      {link.revoked && <span className="tag danger">RÉVOQUÉ · ne répond plus</span>}
      {menuOpen && <LinkMenu link={link} onClose={() => onMenu(false)} actions={actions} />}
    </article>
  );
}

function LinkTable({ links, now, copied, actions }: { links: LinkDto[]; now: Date; copied: string | null; actions: Actions }) {
  return (
    <table className="link-table">
      <thead>
        <tr>
          <th>Nom</th>
          <th>Tableau lié</th>
          <th>Dernier usage</th>
          <th className="num">Appels</th>
          <th>Expiration</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {links.map((link) => {
          const exp = expiry(link, now);
          return (
            <tr key={link.id} className={link.revoked ? 'revoked' : undefined}>
              <td>
                <span className="card-title">{link.name}</span>
                {link.revoked ? <span className="tag danger">RÉVOQUÉ · ne répond plus</span> : <span className="link-url">{urls(link.token).json}</span>}
              </td>
              <td>{link.tableName}</td>
              <td>{sinceLabel(link.lastUsedAt, now)}</td>
              <td className="num mono">{thousands(link.hits)}</td>
              <td className={`tone-${exp.tone}`}>{exp.label}</td>
              <td>
                {!link.revoked && (
                  <div className="table-actions">
                    <CopyButtons link={link} copied={copied} actions={actions} />
                    <button type="button" className="btn btn-secondary btn-34" onClick={() => actions.regenerate(link)}>
                      Régénérer
                    </button>
                    <button type="button" className="btn btn-secondary btn-34" onClick={() => actions.cycleExpiry(link)}>
                      Expiration
                    </button>
                    <button type="button" className="btn btn-danger btn-34" onClick={() => actions.revoke(link)}>
                      Révoquer
                    </button>
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function NewLink({ tables, onCreated }: { tables: TableDto[]; onCreated: (link: LinkDto) => void }) {
  const [name, setName] = useState('');
  const [tableId, setTableId] = useState<number>(tables[0]?.id ?? 0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (tables.length === 0) {
    return (
      <div className="card accent">
        <span className="panel-title">Nouveau lien</span>
        <p className="help">Un lien partage un tableau : créez d’abord un tableau.</p>
        <Link to="/creer" className="btn btn-primary btn-44">
          Créer un tableau
        </Link>
      </div>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onCreated((await api<{ link: LinkDto }>('POST', '/links', { name, tableId })).link);
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  return (
    <form className="card accent" onSubmit={submit}>
      <span className="panel-title">Nouveau lien</span>
      <input className="input input-44" placeholder="Nom (ex. Écran cuisine)" aria-label="Nom du lien" required maxLength={100} value={name} onChange={(event) => setName(event.target.value)} />
      <select className="input input-44" aria-label="Tableau partagé" value={tableId} onChange={(event) => setTableId(Number(event.target.value))}>
        {tables.map((table) => (
          <option key={table.id} value={table.id}>
            {table.name}
          </option>
        ))}
      </select>
      <p className="help">Le lien donne accès aux fenêtres du tableau, sans coordonnées. Il se régénère ou se révoque à tout moment.</p>
      <FormError error={error} />
      <button className="btn btn-primary btn-44" disabled={busy || !name.trim()}>
        Créer le lien
      </button>
    </form>
  );
}
